import { collection, query, where, getDocs, Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { startOfDay, endOfDay, differenceInMinutes, isSameDay } from 'date-fns';

interface BookingSettings {
  maxBookingsPerDay: number;
  maxBookingDuration: number;
  minBookingNotice: number;
  maxAdvanceBooking: number;
  autoConfirm: boolean;
}

export async function validateBooking(
  facilityId: string,
  horseId: string,
  startTime: Date,
  endTime: Date,
  settings: BookingSettings,
  isManager: boolean
) {
  try {
    // Managers can bypass restrictions except capacity
    if (!isManager) {
      const now = new Date();
      
      // Check minimum notice period
      const noticeMinutes = differenceInMinutes(startTime, now);
      if (noticeMinutes < settings.minBookingNotice) {
        throw new Error(
          `Buchungen müssen mindestens ${settings.minBookingNotice} Minuten im Voraus erfolgen`
        );
      }

      // Check maximum advance booking period
      const advanceDays = Math.floor(noticeMinutes / (24 * 60));
      if (advanceDays > settings.maxAdvanceBooking) {
        throw new Error(
          `Buchungen können maximal ${settings.maxAdvanceBooking} Tage im Voraus getätigt werden`
        );
      }

      // Check booking duration
      const durationMinutes = differenceInMinutes(endTime, startTime);
      if (durationMinutes > settings.maxBookingDuration) {
        throw new Error(
          `Die maximale Buchungsdauer beträgt ${settings.maxBookingDuration} Minuten`
        );
      }

      // Check maximum bookings per day for this horse
      const horseBookingsRef = collection(db, 'bookings');
      const horseBookingsQuery = query(
        horseBookingsRef,
        where('horseId', '==', horseId)
      );

      const horseBookingsSnap = await getDocs(horseBookingsQuery);
      
      // Filter bookings for the same day
      const sameDayBookings = horseBookingsSnap.docs.filter(doc => {
        const bookingData = doc.data();
        const bookingStart = bookingData.startTime.toDate();
        return isSameDay(bookingStart, startTime);
      });
      
      // Check for maximum bookings per day
      if (sameDayBookings.length >= settings.maxBookingsPerDay) {
        throw new Error(
          `Maximale Anzahl von ${settings.maxBookingsPerDay} Buchungen pro Tag für dieses Pferd erreicht`
        );
      }
      
      // Check for existing booking in the same timeslot
      const sameTimeBooking = sameDayBookings.find(doc => {
        const bookingData = doc.data();
        const bookingStart = bookingData.startTime.toDate();
        return bookingStart.getHours() === startTime.getHours() &&
               bookingStart.getMinutes() === startTime.getMinutes();
      });
      
      if (sameTimeBooking) {
        throw new Error('Dieses Pferd hat bereits eine Buchung in diesem Zeitslot');
      }
    }

    // Check facility capacity (even managers must respect this)
    const facilityRef = doc(db, 'facilities', facilityId);
    const facilityDoc = await getDoc(facilityRef);
    
    if (!facilityDoc.exists()) {
      throw new Error('Anlage nicht gefunden');
    }

    const facilityData = facilityDoc.data();
    const capacity = facilityData.capacity || 1;

    // Get all bookings for this timeslot
    const bookingsRef = collection(db, 'bookings');
    const bookingsQuery = query(
      bookingsRef,
      where('facilityId', '==', facilityId),
      where('startTime', '>=', Timestamp.fromDate(startOfDay(startTime))),
      where('startTime', '<=', Timestamp.fromDate(endOfDay(startTime)))
    );

    const bookingsSnap = await getDocs(bookingsQuery);
    const overlappingBookings = bookingsSnap.docs.filter(doc => {
      const bookingData = doc.data();
      const bookingStart = bookingData.startTime.toDate();
      return isSameDay(bookingStart, startTime) && 
             bookingStart.getHours() === startTime.getHours() && 
             bookingStart.getMinutes() === startTime.getMinutes();
    });

    // Check if capacity would be exceeded
    if (overlappingBookings.length >= capacity) {
      throw new Error('Dieser Zeitslot ist bereits ausgebucht');
    }

    return true;
  } catch (error: any) {
    throw new Error(error.message);
  }
}
